import React, { useState } from 'react';
import { Drawer } from '../ui/Drawer';
import { Button } from '../ui/Button';
import { usePdfExport } from '../../hooks/usePdfExport';

interface Props {
  open: boolean;
  onClose: () => void;
  count: number;
  statusFilter: string;
  externalPhotographerId?: string;
  externalPhotographerName?: string;
}

export function PerformanceExportPdfDrawer({
  open, onClose, count, statusFilter, externalPhotographerId, externalPhotographerName,
}: Props) {
  const [showEsterno, setShowEsterno] = useState(true);
  const [showServizi, setShowServizi] = useState(true);
  const [showData, setShowData] = useState(true);
  const [showStato, setShowStato] = useState(true);
  const [includeServices, setIncludeServices] = useState(true);
  const [includeAssignments, setIncludeAssignments] = useState(true);
  const [validUntil, setValidUntil] = useState('');
  const [notes, setNotes] = useState('');
  const { isExporting, exportPdf } = usePdfExport();
  const isExternalMode = !!externalPhotographerId;

  const handleExport = async () => {
    const ok = await exportPdf('/api/performances/export-performances-pdf', {
      status: statusFilter || undefined,
      externalId: externalPhotographerId || undefined,
      visibleColumns: { esterno: showEsterno, servizi: showServizi, data: showData, stato: showStato },
      includeServices,
      includeAssignments,
      validUntil: validUntil || undefined,
      notes: notes || undefined,
    }, 'report-prestazioni.pdf');
    if (ok) onClose();
  };

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title={`Esporta PDF Prestazioni${isExternalMode ? ` — ${externalPhotographerName}` : ''}`}
      width={480}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>Annulla</Button>
          <Button onClick={handleExport} loading={isExporting}>Genera PDF</Button>
        </>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
        <p style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)' }}>
          Esportazione basata sui filtri attivi &middot; <strong>{count}</strong> prestazioni trovate
        </p>

        {/* Column visibility */}
        <div>
          <label style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)', fontWeight: 600, display: 'block', marginBottom: '0.5rem' }}>
            Colonne visibili
          </label>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.375rem' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: 'var(--text-sm)', cursor: 'pointer' }}>
              <input type="checkbox" checked={showEsterno} onChange={e => setShowEsterno(e.target.checked)} style={{ accentColor: 'var(--color-primary)' }} />
              Esterno (nome fotografo)
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: 'var(--text-sm)', cursor: 'pointer' }}>
              <input type="checkbox" checked={showServizi} onChange={e => setShowServizi(e.target.checked)} style={{ accentColor: 'var(--color-primary)' }} />
              Servizi (nomi)
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: 'var(--text-sm)', cursor: 'pointer' }}>
              <input type="checkbox" checked={showData} onChange={e => setShowData(e.target.checked)} style={{ accentColor: 'var(--color-primary)' }} />
              Data evento
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: 'var(--text-sm)', cursor: 'pointer' }}>
              <input type="checkbox" checked={showStato} onChange={e => setShowStato(e.target.checked)} style={{ accentColor: 'var(--color-primary)' }} />
              Stato
            </label>
          </div>
        </div>

        {/* Detail toggles */}
        <div>
          <label style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)', fontWeight: 600, display: 'block', marginBottom: '0.5rem' }}>
            Dettaglio per prestazione
          </label>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.375rem' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: 'var(--text-sm)', cursor: 'pointer' }}>
              <input type="checkbox" checked={includeServices} onChange={e => setIncludeServices(e.target.checked)} style={{ accentColor: 'var(--color-primary)' }} />
              Includi dettaglio servizi (tabella per prestazione)
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: 'var(--text-sm)', cursor: 'pointer' }}>
              <input type="checkbox" checked={includeAssignments} onChange={e => setIncludeAssignments(e.target.checked)} style={{ accentColor: 'var(--color-primary)' }} />
              Includi dettaglio assegnazioni
            </label>
          </div>
        </div>

        {/* Valid until */}
        <div>
          <label style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)', fontWeight: 600, display: 'block', marginBottom: '0.375rem' }}>Valido fino al</label>
          <input
            type="date"
            value={validUntil}
            onChange={e => setValidUntil(e.target.value)}
            style={{
              width: '100%', background: 'var(--glass-bg-card)',
              border: '1px solid rgba(255,255,255,0.10)', borderRadius: 'var(--radius-card)',
              color: 'var(--color-text)', fontSize: 'var(--text-sm)',
              padding: '0.5rem 0.75rem', outline: 'none', boxSizing: 'border-box',
            }}
          />
        </div>

        {/* Notes */}
        <div>
          <label style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)', fontWeight: 600, display: 'block', marginBottom: '0.375rem' }}>
            {isExternalMode ? 'Condizioni commerciali riservate (max 500 caratteri)' : 'Note (max 500 caratteri)'}
          </label>
          <textarea
            value={notes}
            onChange={e => setNotes(e.target.value.slice(0, 500))}
            rows={3}
            placeholder={isExternalMode ? 'Condizioni riservate per il fotografo esterno...' : 'Note da includere sotto l\'intestazione...'}
            style={{
              width: '100%', background: 'var(--glass-bg-card)',
              border: '1px solid rgba(255,255,255,0.10)', borderRadius: 'var(--radius-card)',
              color: 'var(--color-text)', fontSize: 'var(--text-sm)',
              padding: '0.5rem 0.75rem', resize: 'vertical', outline: 'none',
              boxSizing: 'border-box',
            }}
          />
        </div>
      </div>
    </Drawer>
  );
}
